import { useMemo } from "react";
import { Trash2 } from "lucide-react";
import type { printOnDemand } from "common/utils/models";
import { Button } from "@design-lab/shared/ui";
import { useCompositionForm } from "@design-lab/entities/composition";
import { buildQuantitiesForSummary } from "@design-lab/entities/composition/lib/design-project/composition";
import { ArtworkPreview } from "@design-lab/features/manage-product-sizing/ui/ArtworkPreview";

// Using the type directly to avoid extension issues
type Composition = printOnDemand.getProduct.Composition;

interface CompositionSizeGridProps {
  productName: string;
  brand: string;
  brandLogo: string;
  catalogId: string;
  onRemoveComposition: () => void;
  composition: Composition;
  showRemoveButton: boolean;
}

export function CompositionSizeGrid({
  productName,
  brand,
  catalogId,
  onRemoveComposition,
  composition,
  showRemoveButton
}: CompositionSizeGridProps) {
  const { form } = useCompositionForm();

  const summaryQuantities = useMemo(
    () => buildQuantitiesForSummary(composition, form.getValues().addedCompositions),
    [composition, form]
  );
  const productFullName = `${brand} ${catalogId} - ${productName}`;

  if (!composition) return null;

  return (
    <div className="flex w-full items-start gap-2 lg:gap-5">
      <div className="relative shrink-0">
        <ArtworkPreview
          composition={composition}
          onClick={() => form.setValue("currentCompositionId", composition.id)}
        />
        {showRemoveButton && (
          <Button
            data-testid="remove-composition-button"
            variant="icon"
            size="icon"
            className="bg-background absolute left-2.5 top-2.5 flex items-center justify-center rounded border border-gray-900 p-0 hover:bg-red-50"
            onPress={onRemoveComposition}
            aria-label="Remove composition"
          >
            <Trash2 className="size-4 text-gray-900" />
          </Button>
        )}
      </div>

      <div className="flex flex-1 items-start justify-between">
        <div className="flex min-w-0 flex-1 flex-col justify-start">
          <div className="mb-2 flex items-start gap-3">
            <h3 className="text-foreground min-w-0 flex-1 text-sm font-semibold leading-tight lg:text-lg">
              {productFullName}
            </h3>
          </div>
          <p className="mb-1 text-sm text-gray-600">
            Color: <span className="text-foreground">{composition.colorName}</span>
          </p>

          {Object.keys(summaryQuantities).length > 0 && (
            <>
              <p className="mb-1 text-sm text-gray-500">Size & Quantity: </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(summaryQuantities).map(([size, quantity]) => (
                  <p key={size} className="rounded bg-gray-200 p-1 text-xs text-gray-500">
                    {size} <span className="font-bold text-gray-700">{quantity}</span>
                  </p>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
